/**
 * Terminkategorien-Editor — Sub-Block in der KI-Zentrale unter Terminplanung.
 *
 * Pflegt pro Org Termin-Typen mit Default-Dauer + Default-Mitarbeiter. Der
 * ElevenLabs/HeyKiki-Agent referenziert die Kategorien im System-Prompt und
 * gibt den Namen beim bookAppointment-Aufruf als `kategorie`-Parameter mit.
 * Backend matcht case-insensitive und übernimmt die Defaults beim Anlegen
 * des Termins. Beim manuellen Bestätigen (Aufklappfeld) ebenfalls verfügbar.
 */
import { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown } from '../../icons/wp-icons';
import {
  getTerminKategorien,
  createTerminKategorie,
  updateTerminKategorie,
  deleteTerminKategorie,
  reorderTerminKategorien,
} from '../../api/terminKategorienApi';
import { getAllBenutzer } from '../../api/benutzerApi';
import toast from 'react-hot-toast';

export default function TerminKategorienEditor({
  readOnly = false,
  filterAktiv = false,
  onFilterToggle = null,
  isHeykikiOrg = false,
}) {
  // Org-aware Microcopy: HeyKiki spricht von „Kiki", Standalone vom „KI-Agent".
  const A = isHeykikiOrg ? 'Kiki' : 'der KI-Agent';
  const Acap = isHeykikiOrg ? 'Kiki' : 'Der KI-Agent';
  const [kategorien, setKategorien] = useState([]);
  const [benutzer, setBenutzer] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getTerminKategorien().catch(() => []), getAllBenutzer({ aktiv: 'true' }).catch(() => [])])
      .then(([kats, bens]) => {
        if (cancelled) return;
        setKategorien(Array.isArray(kats) ? kats : []);
        setBenutzer(Array.isArray(bens) ? bens : []);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const updateLocal = (id, patch) => {
    setKategorien(prev => prev.map(k => (k.id === id ? { ...k, ...patch } : k)));
  };

  // Toast-Logik: der Sync läuft jetzt fire-and-forget (sonst blockierte er
  // den PUT bis zu 35s, was wie „Save tut nichts" wirkte). Wir zeigen daher
  // direkt „Gespeichert · Live-Agent wird aktualisiert" — etwaige Sync-Fehler
  // landen in den Backend-Logs, nicht mehr als Toast.
  const showSaveToast = (sync) => {
    if (sync?.triggered) {
      toast.success('Gespeichert · Live-Agent wird aktualisiert');
    } else if (sync?.skipped) {
      toast.success('Einstellungen gespeichert');
    } else {
      toast.success('Einstellungen gespeichert');
    }
  };

  const persist = async (id, payload) => {
    try {
      const updated = await updateTerminKategorie(id, payload);
      const { _sync, ...kat } = updated || {};
      setKategorien(prev => prev.map(k => (k.id === id ? kat : k)));
      showSaveToast(_sync);
    } catch (e) {
      toast.error('Speichern fehlgeschlagen: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleAdd = async () => {
    try {
      const created = await createTerminKategorie({ name: 'Neue Kategorie', defaultDauerMinuten: 60 });
      const { _sync, ...kat } = created || {};
      setKategorien(prev => [...prev, kat]);
      showSaveToast(_sync);
    } catch (e) {
      toast.error('Anlegen fehlgeschlagen: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kategorie wirklich löschen? Bestehende Termine behalten ihre Daten, verlieren aber die Verknüpfung.')) return;
    try {
      const result = await deleteTerminKategorie(id);
      setKategorien(prev => prev.filter(k => k.id !== id));
      showSaveToast(result?._sync);
    } catch (e) {
      toast.error('Löschen fehlgeschlagen: ' + (e.response?.data?.error || e.message));
    }
  };

  const move = async (id, direction) => {
    const idx = kategorien.findIndex(k => k.id === id);
    if (idx < 0) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= kategorien.length) return;
    const next = [...kategorien];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setKategorien(next);
    try {
      const result = await reorderTerminKategorien(next.map(k => k.id));
      showSaveToast(result?._sync);
    } catch (e) {
      toast.error('Reihenfolge konnte nicht gespeichert werden');
    }
  };

  if (loading) {
    return <p className="text-xs text-text-muted">Lade Terminkategorien…</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-sm font-medium text-text">Terminkategorien</p>
          <p className="text-xs text-text-muted mt-0.5">
            Eigene Termin-Typen (z. B. „Wartung", „Reparatur", „Notfall") mit Standard-Dauer und Standard-Mitarbeiter.
            Der KI-Agent erkennt sie im Gespräch und legt Termine direkt mit den passenden Defaults an.
          </p>
        </div>
      </div>

      {/* Master-Toggle: schränkt Kiki auf bestimmte Kategorien ein. Default OFF =
          alle Anliegen gehen in den Termin-Workflow (heutiges Verhalten). ON =
          nur Kategorien mit Häkchen werden gebucht, alle anderen Anliegen
          enden mit „Kollege meldet sich". */}
      {onFilterToggle && (
        <div className="border border-border rounded-lg p-3 bg-blue-50/30">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!filterAktiv}
              onChange={(e) => onFilterToggle(e.target.checked)}
              disabled={readOnly}
              className="mt-0.5 w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <div>
              <div className="text-sm font-medium text-text">
                {Acap} darf nur bestimmte Terminkategorien buchen
              </div>
              <div className="text-xs text-text-muted mt-0.5">
                {filterAktiv
                  ? `Aktiv — ${A} bucht nur für die unten ausgewählten Kategorien. Bei allen anderen Anliegen: „Ein Kollege meldet sich."`
                  : `Inaktiv — ${A} bucht Termine für alle Anliegen, die zu einer der Kategorien passen.`}
              </div>
            </div>
          </label>
        </div>
      )}

      {kategorien.length === 0 ? (
        <p className="text-xs text-text-muted italic py-2">Noch keine Kategorien angelegt.</p>
      ) : (
        <div className="space-y-2">
          {kategorien.map((k, idx) => (
            <div key={k.id} className="border border-border rounded-lg p-3 bg-white">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
                <div className="sm:col-span-3">
                  <label className="block text-[11px] font-medium text-text-muted mb-1">Name</label>
                  <input
                    type="text"
                    value={k.name || ''}
                    onChange={e => updateLocal(k.id, { name: e.target.value })}
                    onBlur={() => persist(k.id, { name: k.name })}
                    disabled={readOnly}
                    className="w-full px-2 py-1.5 text-sm border border-border rounded-md"
                  />
                </div>
                <div className="sm:col-span-5">
                  <label className="block text-[11px] font-medium text-text-muted mb-1">Beschreibung (für KI-Agent)</label>
                  <input
                    type="text"
                    value={k.beschreibung || ''}
                    onChange={e => updateLocal(k.id, { beschreibung: e.target.value })}
                    onBlur={() => persist(k.id, { beschreibung: k.beschreibung })}
                    disabled={readOnly}
                    placeholder="Wann diese Kategorie? z.B. Regelmäßige Wartung Heizung/Therme"
                    className="w-full px-2 py-1.5 text-sm border border-border rounded-md"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-medium text-text-muted mb-1">Dauer</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={5}
                      max={1440}
                      step={5}
                      value={k.defaultDauerMinuten ?? ''}
                      onChange={e => updateLocal(k.id, { defaultDauerMinuten: e.target.value === '' ? '' : Number(e.target.value) })}
                      onBlur={() => {
                        const n = Number(k.defaultDauerMinuten);
                        if (Number.isFinite(n) && n >= 5 && n <= 1440) persist(k.id, { defaultDauerMinuten: n });
                      }}
                      disabled={readOnly}
                      className="w-16 px-2 py-1.5 text-sm border border-border rounded-md"
                    />
                    <span className="text-xs text-text-muted">Min</span>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-medium text-text-muted mb-1">Mitarbeiter</label>
                  <select
                    value={k.defaultBearbeiterId || ''}
                    onChange={e => {
                      const v = e.target.value || null;
                      updateLocal(k.id, { defaultBearbeiterId: v });
                      persist(k.id, { defaultBearbeiterId: v });
                    }}
                    disabled={readOnly}
                    className="w-full px-2 py-1.5 text-sm border border-border rounded-md bg-white"
                  >
                    <option value="">— keiner —</option>
                    {benutzer.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Pro-Kategorie-Häkchen — nur sichtbar wenn Master-Toggle aktiv.
                  Default kikiBuchungErlaubt=true (DB-Default), das heißt beim
                  ersten Aktivieren des Masters sind alle Kategorien erlaubt;
                  der Admin entfernt gezielt die Häkchen. */}
              {filterAktiv && (
                <div className="mt-2 pt-2 border-t border-border-light">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={k.kikiBuchungErlaubt !== false}
                      onChange={(e) => {
                        const v = e.target.checked;
                        updateLocal(k.id, { kikiBuchungErlaubt: v });
                        persist(k.id, { kikiBuchungErlaubt: v });
                      }}
                      disabled={readOnly}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-xs text-text">
                      {Acap} darf Termine für „{k.name || 'diese Kategorie'}" buchen
                    </span>
                  </label>
                </div>
              )}
              <div className="flex items-center justify-end gap-1 mt-2">
                <button
                  type="button"
                  onClick={() => move(k.id, 'up')}
                  disabled={readOnly || idx === 0}
                  className="p-1 text-text-muted hover:text-text disabled:opacity-30"
                  aria-label="Nach oben"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => move(k.id, 'down')}
                  disabled={readOnly || idx === kategorien.length - 1}
                  className="p-1 text-text-muted hover:text-text disabled:opacity-30"
                  aria-label="Nach unten"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(k.id)}
                  disabled={readOnly}
                  className="p-1 text-red-500 hover:text-red-700 disabled:opacity-30"
                  aria-label="Löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={handleAdd}
        disabled={readOnly}
        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
      >
        <Plus className="w-4 h-4" />
        Kategorie hinzufügen
      </button>
    </div>
  );
}
